import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { networkApi } from '../../../lib/api/network.api';
import { errorText } from '../../../lib/error-text';

// ─── Revocar una certificación ya otorgada ──────────────────────────────────
//
// ES EL ACTO MÁS CARO DE ESTE MÓDULO. Otorgar se hace al final de una revisión,
// con el expediente delante y una decisión que el centro está esperando.
// Revocar es lo contrario: ocurre meses después, sin expediente en pantalla, y
// lo que el centro recibe es que algo que decía «vigente» dejó de decirlo.
//
// DOS FRENOS, Y CADA UNO ATAJA UN ERROR DISTINTO:
//  · ESCRIBIR EL NOMBRE DEL PROGRAMA ataja el error de FILA. La tabla lista las
//    certificaciones de todos los centros; revocar la de al lado es un clic de
//    distancia y no hay nada en la pantalla que lo delate. Es el mismo freno
//    que el director eligió para graduar: el nombre del PROGRAMA, no el de la
//    persona.
//  · EL MOTIVO ataja el error de SILENCIO. El espejo del centro no lleva quién
//    revocó —esa asimetría es deliberada—, así que el motivo es LO ÚNICO que la
//    institución va a tener para entender qué pasó. Un motivo vacío deja a
//    alguien mirando una certificación caída sin saber por qué.
//
// El servidor exige las dos cosas igual; acá se piden antes para que el error
// no llegue como un rechazo después de haber apretado.

export function RevokeCertificationDialog({
  open,
  onClose,
  onRevoked,
  tenantId,
  certification,
}: {
  open: boolean;
  onClose: () => void;
  /** Se llama tras revocar, para que la pantalla vuelva a leer. */
  onRevoked: () => void;
  /** El tenant de la RED: revocar es un acto suyo. */
  tenantId: string;
  certification: { id: string; programName: string; capCode: string; centerName: string } | null;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setTyped('');
      setError(null);
    }
  }, [open]);

  const programName = certification?.programName ?? '';
  // Sin distinguir mayúsculas ni espacios de los bordes: el freno existe para
  // que alguien LEA qué está revocando, no para que teclee con exactitud.
  const typedMatches =
    programName.trim() !== '' &&
    typed.trim().toLocaleLowerCase() === programName.trim().toLocaleLowerCase();
  // El mínimo es el mismo que el servidor exige. Repetirlo acá es lo que hace
  // que el botón explique por qué no se puede, en vez de fallar después.
  const reasonLongEnough = reason.trim().length >= 10;
  const canRevoke = !!certification && typedMatches && reasonLongEnough && !busy;

  const handleRevoke = async () => {
    if (!certification || !canRevoke) return;
    setBusy(true);
    setError(null);
    try {
      await networkApi.revokeCapCertification({
        tenantId,
        certificationId: certification.id,
        reason: reason.trim(),
      });
      onRevoked();
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('network.dashboard.certifications.revokeTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">
            {t('network.dashboard.certifications.revokeWarning', {
              program: programName,
              center: certification?.centerName ?? '',
              cap: certification?.capCode ?? '',
            })}
          </Alert>

          <TextField
            label={t('network.dashboard.certifications.revokeReasonLabel')}
            helperText={t('network.dashboard.certifications.revokeReasonHelp')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            disabled={busy}
            error={reason.length > 0 && !reasonLongEnough}
          />

          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {t('network.dashboard.certifications.revokeTypeToConfirm', { program: programName })}
            </Typography>
            <TextField
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              fullWidth
              disabled={busy}
              error={typed.length > 0 && !typedMatches}
              slotProps={{ htmlInput: { 'aria-label': programName } }}
            />
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common:cancel')}
        </Button>
        <Button color="error" variant="contained" onClick={handleRevoke} disabled={!canRevoke}>
          {t('network.dashboard.certifications.revokeConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
